import type { FactionId } from '@archon3d/core';
import type { SessionSnapshot } from '../game/session';

/**
 * A moment the one device at the table has to change hands: a person's turn on the board, their
 * secret support choice in Card mode, or their answer to a Muster call. The key names the moment
 * so that acknowledging it once is enough, and a new turn is a new moment.
 */
export interface HandOver {
  key: string;
  faction: FactionId;
  reason: 'turn' | 'card' | 'muster';
}

/**
 * The hand-over waiting to be acknowledged, if the people at this device take turns: none when
 * one person plays, when they have a controller each, online, or when the device is already with
 * the side the moment belongs to. It is with whoever acknowledged the last one, so an attacker who
 * has just moved is not asked for the device again for the Card choice that follows
 * (client-tablets-ui:bugs#6).
 */
export function pendingHandOver(
  snapshot: SessionSnapshot,
  oneDevice: boolean,
  acknowledged: Pick<HandOver, 'key' | 'faction'> | null,
): HandOver | null {
  if (!oneDevice || snapshot.online) return null;
  const { state, seats } = snapshot;
  const people = Object.values(seats).filter((seat) => seat === 'human').length;
  if (people < 2) return null;
  const clash = snapshot.cardClash;
  const muster = snapshot.muster;
  const moment: HandOver | null =
    clash?.phase === 'choose' && clash.chooser && seats[clash.chooser] === 'human'
      ? { key: `card:${state.turn}:${clash.chooser}`, faction: clash.chooser, reason: 'card' }
      : muster && seats[muster.faction] === 'human'
        ? {
            key: `muster:${state.turn}:${muster.faction}`,
            faction: muster.faction,
            reason: 'muster',
          }
        : state.phase === 'play' && seats[state.activeFaction] === 'human' && !snapshot.busy
          ? {
              key: `turn:${state.turn}:${state.activeFaction}`,
              faction: state.activeFaction,
              reason: 'turn',
            }
          : null;
  return moment && moment.faction !== acknowledged?.faction ? moment : null;
}
